import React, { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";

const useCountUp = (target, duration = 1200) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); io.disconnect(); } },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setCount(target); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(p * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);

  return [count, ref];
};

const About = () => {
  const [events, eventsRef] = useCountUp(120);
  const [years, yearsRef] = useCountUp(8);

  return (
    <section
      id="sobre"
      className="bg-black py-24 md:py-32 border-t border-white/10"
      data-testid="about-section"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="divider-red" />
              <span className="font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase">03 — Sobre</span>
            </div>
            <h2 className="font-heading text-white text-6xl md:text-7xl uppercase leading-[0.9]" data-testid="about-title">
              Quem<br />Sou
            </h2>
          </Reveal>
        </div>

        <div className="lg:col-span-8 space-y-7 text-white/85 text-lg md:text-xl leading-relaxed">
          <Reveal delay={100}>
            <p data-testid="about-p1">
              Sou Miguel Faria. Fotógrafo. Português. Apaixonado.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p data-testid="about-p2">
              Cresci a ver gente a correr, a saltar, a cair, a levantar-se. Hoje, é a minha câmara que conta essas histórias.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <p data-testid="about-p3">
              Capto emoção. Capto suor. Capto o segundo em que tudo acontece — e ninguém mais o vê.
            </p>
          </Reveal>
          <Reveal delay={400}>
            <p data-testid="about-p4">
              Desporto. Retrato artístico. Ação crua. Movimento puro.
            </p>
          </Reveal>
          <Reveal delay={500}>
            <p data-testid="about-p5">
              Trabalho em Portugal. Viajo para onde for preciso.
            </p>
          </Reveal>
          <Reveal delay={600}>
            <div className="pt-4 grid grid-cols-3 gap-6 border-t border-white/15">
              <div ref={eventsRef}>
                <div className="font-heading text-5xl text-[#c0392b]" data-testid="stat-1">{events}+</div>
                <div className="font-heading text-xs tracking-[0.25em] uppercase text-white/60 mt-2">Eventos</div>
              </div>
              <div ref={yearsRef}>
                <div className="font-heading text-5xl text-[#c0392b]" data-testid="stat-2">0{years}</div>
                <div className="font-heading text-xs tracking-[0.25em] uppercase text-white/60 mt-2">Anos</div>
              </div>
              <div>
                <div className="font-heading text-5xl text-[#c0392b]" data-testid="stat-3">PT</div>
                <div className="font-heading text-xs tracking-[0.25em] uppercase text-white/60 mt-2">Base</div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
